import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import PortalModal from "../PortalModal";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

export default function ChallanDetails() {
  const [idNumber, setIdNumber] = useState("");
  const [chassisNumber, setChassisNumber] = useState("");
  const [engineNumber, setEngineNumber] = useState("");
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [challanData, setChallanData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [challanAccess, setChallanAccess] = useState(false);
  const [challanPrice, setChallanPrice] = useState(null);

  // Separate filters for history and current results
  const [historyFilters, setHistoryFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [currentFilters, setCurrentFilters] = useState({
    search: "",
    status: "",
    fromDate: "",
    toDate: "",
  });

  const [filteredCurrentData, setFilteredCurrentData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // State for API response data modal
  const [apiResponseData, setApiResponseData] = useState(null);
  const [showApiResponseModal, setShowApiResponseModal] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/get-document-records?service=challan`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const historyData = response.data.map((item, index) => ({
        date: new Date(item.createdAt).toLocaleString(),
        slNo: `${index + 1}`.padStart(3, "0"),
        rcNumber: item.idNumber,
        chassisNumber: item.apiResponse?.chassis_number || "N/A",
        engineNumber: item.apiResponse?.engine_number || "N/A",
        name: item.apiResponse?.name,
        status:
          item.status === "pending" || item.status === "failed"
            ? "Failed"
            : "Success",
        error: item.status === "failed" ? "Verification failed" : "",
        createdAt: new Date(item.createdAt),
        challans: item.apiResponse?.challans || [],
      }));

      setHistory(historyData);
      setFilteredHistory(historyData);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = (data) => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.text("Challan Details Report", 105, 15, { align: "center" });

    // Vehicle Information
    doc.setFontSize(12);
    doc.text(`RC Number: ${idNumber}`, 14, 25);
    doc.text(`Chassis Number: ${chassisNumber}`, 14, 35);
    doc.text(`Engine Number: ${engineNumber}`, 14, 45);

    // Table for challan details
    autoTable(doc, {
      startY: 50,
      head: [
        ["#", "Challan No", "Date", "Offense", "Amount", "Status", "State"],
      ],
      body: data.challans.map((c, idx) => [
        idx + 1,
        c.challan_number,
        c.challan_date,
        c.offense_details,
        `${c.amount}`,
        c.challan_status,
        c.state,
      ]),
      theme: "grid",
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240],
      },
    });

    doc.save(`challan_report_${idNumber}.pdf`);
  };

  const fetchPriceData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/services/access-check`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setChallanAccess(response.data?.serviceAccess.includes("challan"));
      setChallanPrice(response.data?.servicePrices?.challan);
    } catch (e) {
      console.error("Error fetching access data:", e);
    }
  };

  const proceedWithApiCall = async () => {
    setIsLoading(true);
    setError("");
    setChallanData([]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/services/rc/challan-details`,
        {
          id_number: idNumber,
          chassis_number: chassisNumber,
          engine_number: engineNumber,
          state_only: false,
          state_portal: ["DL", "TS", "KA", "GJ"],
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Failed to fetch challan data"
        );
      }

      setChallanData(response.data.data.challans || []);
      setFilteredCurrentData(response.data.data.challans || []);
      setApiResponseData(response.data);
      setShowApiResponseModal(true);
      toast.success("Challan data fetched successfully");

      // Refresh history after successful API call
      fetchData();
    } catch (err) {
      console.error("Error fetching challan data:", err);
      let errorMessage = "Failed to fetch challan data. Please try again.";
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    setError("");
    if (!idNumber || !chassisNumber || !engineNumber) {
      return setError("Please enter all fields!");
    }
    if (!challanAccess) {
      return setError("You don't have access to this service");
    }
    setShowConfirmationModal(true);
  };

  const applyHistoryFilters = () => {
    let data = [...history];

    if (historyFilters.search) {
      const term = historyFilters.search.toLowerCase();
      data = data.filter(
        (item) =>
          item.rcNumber.toLowerCase().includes(term) ||
          item.chassisNumber.toLowerCase().includes(term) ||
          item.engineNumber.toLowerCase().includes(term)
      );
    }

    if (historyFilters.status) {
      data = data.filter(
        (item) =>
          item.status.toLowerCase() === historyFilters.status.toLowerCase()
      );
    }

    if (historyFilters.fromDate || historyFilters.toDate) {
      const fromDate = historyFilters.fromDate
        ? new Date(historyFilters.fromDate)
        : null;
      const toDate = historyFilters.toDate
        ? new Date(historyFilters.toDate)
        : null;
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);

      data = data.filter((item) => {
        const itemDate = new Date(item.createdAt);
        if (fromDate && itemDate < fromDate) return false;
        if (toDate && itemDate > toDate) return false;
        return true;
      });
    }

    setFilteredHistory(data);
  };

  const applyCurrentFilters = () => {
    let data = [...challanData];

    if (currentFilters.search) {
      const term = currentFilters.search.toLowerCase();
      data = data.filter(
        (c) =>
          c.challan_number.toLowerCase().includes(term) ||
          c.offense_details.toLowerCase().includes(term)
      );
    }

    if (currentFilters.status) {
      data = data.filter(
        (c) =>
          c.challan_status.toLowerCase() === currentFilters.status.toLowerCase()
      );
    }

    if (currentFilters.fromDate || currentFilters.toDate) {
      const fromDate = currentFilters.fromDate
        ? new Date(currentFilters.fromDate)
        : null;
      const toDate = currentFilters.toDate
        ? new Date(currentFilters.toDate)
        : null;
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);

      data = data.filter((c) => {
        const challanDate = new Date(c.challan_date_time);
        if (fromDate && challanDate < fromDate) return false;
        if (toDate && challanDate > toDate) return false;
        return true;
      });
    }

    setFilteredCurrentData(data);
    setCurrentPage(1);
  };

  const clearHistoryFilters = () => {
    setHistoryFilters({ search: "", status: "", fromDate: "", toDate: "" });
    setFilteredHistory(history);
  };

  const clearCurrentFilters = () => {
    setCurrentFilters({ search: "", status: "", fromDate: "", toDate: "" });
    setFilteredCurrentData(challanData);
  };

  const totalPages = Math.ceil(filteredCurrentData.length / itemsPerPage);
  const currentData = filteredCurrentData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    fetchPriceData();
    fetchData();
  }, []);

  useEffect(() => {
    applyHistoryFilters();
  }, [historyFilters, history]);

  useEffect(() => {
    applyCurrentFilters();
  }, [currentFilters, challanData]);

  return (
    <div className="py-4 w-full">
      <div className="bg-blue-100 p-2 mb-4">
        <h2 className="text-center text-xl font-semibold text-blue-700">
          Challan Details
        </h2>
      </div>

      {/* Input Fields */}
      <div className="py-4 space-y-4">
        <div className="w-full flex gap-4 items-center">
          <input
            type="text"
            placeholder="Enter RC No"
            value={idNumber}
            onChange={(e) => setIdNumber(e.target.value.toUpperCase())}
            className="w-1/4 px-4 py-2 border rounded-md"
          />
          <input
            type="text"
            placeholder="Enter Chassis No"
            value={chassisNumber}
            onChange={(e) => setChassisNumber(e.target.value.toUpperCase())}
            className="w-1/4  px-4 py-2 border rounded-md"
          />
          <input
            type="text"
            placeholder="Enter Engine No"
            value={engineNumber}
            onChange={(e) => setEngineNumber(e.target.value.toUpperCase())}
            className="w-1/4  px-4 py-2 border rounded-md"
          />
        </div>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !idNumber || !chassisNumber || !engineNumber}
          className={`bg-indigo-600 text-white py-2 px-6 rounded hover:bg-indigo-700 ${
            isLoading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isLoading ? "Processing..." : "SUBMIT"}
        </button>

        {error && <p className="text-red-500 text-sm">{error}</p>}
      </div>

      {/* History Table */}
      <div className="bg-white p-4 shadow-md rounded-lg mb-4">
        <h3 className="text-lg font-semibold mb-4">Challan History</h3>

        {/* History Filters */}
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <h4 className="text-md font-medium mb-3">Filter History</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Search</label>
              <input
                type="text"
                placeholder="Search RC, Chassis, Engine..."
                value={historyFilters.search}
                onChange={(e) =>
                  setHistoryFilters({
                    ...historyFilters,
                    search: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={historyFilters.status}
                onChange={(e) =>
                  setHistoryFilters({
                    ...historyFilters,
                    status: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
              >
                <option value="">All</option>
                <option value="Success">Success</option>
                <option value="Failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                From Date
              </label>
              <input
                type="date"
                value={historyFilters.fromDate}
                onChange={(e) =>
                  setHistoryFilters({
                    ...historyFilters,
                    fromDate: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">To Date</label>
              <input
                type="date"
                value={historyFilters.toDate}
                onChange={(e) =>
                  setHistoryFilters({
                    ...historyFilters,
                    toDate: e.target.value,
                  })
                }
                className="w-full border rounded px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={clearHistoryFilters}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Clear
            </button>
            <button
              onClick={applyHistoryFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2">SL No</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">RC Number</th>
                <th className="px-4 py-2">Chassis Number</th>
                <th className="px-4 py-2">Engine Number</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Challans Found</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.length > 0 ? (
                filteredHistory.map((item, idx) => (
                  <tr key={idx} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-2">{item.slNo}</td>
                    <td className="px-4 py-2">{item.date}</td>
                    <td className="px-4 py-2">{item.rcNumber}</td>
                    <td className="px-4 py-2">{item.chassisNumber}</td>
                    <td className="px-4 py-2">{item.engineNumber}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`px-2 py-1 rounded text-white text-xs ${
                          item.status === "Success"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">{item.challans.length}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No history records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Current Challan Table */}
      {challanData.length > 0 && (
        <div className="bg-white p-4 shadow-md rounded-lg mb-4">
          <h3 className="text-lg font-semibold mb-4">
            Current Challan Results
          </h3>

          {/* Current Results Filters */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <h4 className="text-md font-medium mb-3">Filter Current Results</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search Challan No, Offense..."
                  value={currentFilters.search}
                  onChange={(e) =>
                    setCurrentFilters({
                      ...currentFilters,
                      search: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={currentFilters.status}
                  onChange={(e) =>
                    setCurrentFilters({
                      ...currentFilters,
                      status: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Disposed">Disposed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={currentFilters.fromDate}
                  onChange={(e) =>
                    setCurrentFilters({
                      ...currentFilters,
                      fromDate: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={currentFilters.toDate}
                  onChange={(e) =>
                    setCurrentFilters({
                      ...currentFilters,
                      toDate: e.target.value,
                    })
                  }
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={clearCurrentFilters}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Clear
              </button>
              <button
                onClick={applyCurrentFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Apply
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Challan No</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Offense</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length > 0 ? (
                  currentData.map((c, idx) => (
                    <tr key={idx} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td className="px-4 py-2">{c.challan_number}</td>
                      <td className="px-4 py-2">{c.challan_date}</td>
                      <td className="px-4 py-2">{c.offense_details}</td>
                      <td className="px-4 py-2">₹{c.amount}</td>
                      <td className="px-4 py-2">
                        <span
                          className={`px-2 py-1 rounded text-white text-xs ${
                            c.challan_status === "Pending"
                              ? "bg-red-500"
                              : "bg-green-500"
                          }`}
                        >
                          {c.challan_status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-500">
                      No challan records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <div>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredCurrentData.length
                )}{" "}
                of {filteredCurrentData.length}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded ${
                      currentPage === i + 1
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <PortalModal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold mb-4">Confirm Payment</h3>
              <p className="mb-4">
                This Challan fetch will cost you{" "}
                <span className="text-red-600">₹{challanPrice}</span>. Proceed?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowConfirmationModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowConfirmationModal(false);
                    proceedWithApiCall();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Confirm & Pay
                </button>
              </div>
            </div>
          </div>
        </PortalModal>
      )}

      {/* API Response Data Modal */}
      {showApiResponseModal && apiResponseData && (
        <PortalModal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-screen overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Challan Details Report
                </h3>
                <button
                  onClick={() => setShowApiResponseModal(false)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ✕
                </button>
              </div>

              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <h4 className="font-medium text-blue-800 mb-2">
                  Vehicle Information
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">RC Number</p>
                    <p className="font-medium">{idNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Chassis Number</p>
                    <p className="font-medium">{chassisNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Engine Number</p>
                    <p className="font-medium">{engineNumber}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <h4 className="font-medium text-gray-800 mb-2">Summary</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Total Challans</p>
                    <p className="font-medium">
                      {apiResponseData.data.challans.length}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Successful Backends</p>
                    <p className="font-medium">
                      {apiResponseData.data.successful_backends.join(", ")}
                    </p>
                  </div>
                  {apiResponseData.data.failed_backends.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-sm text-gray-600">Failed Backends</p>
                      <p className="font-medium text-red-600">
                        {apiResponseData.data.failed_backends.join(", ")}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-3">
                  Challan Details
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 border">#</th>
                        <th className="px-4 py-2 border">Challan No</th>
                        <th className="px-4 py-2 border">Date</th>
                        <th className="px-4 py-2 border">Offense</th>
                        <th className="px-4 py-2 border">Amount</th>
                        <th className="px-4 py-2 border">Status</th>
                        <th className="px-4 py-2 border">State</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apiResponseData.data.challans.map((c, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2 border">{idx + 1}</td>
                          <td className="px-4 py-2 border">
                            {c.challan_number}
                          </td>
                          <td className="px-4 py-2 border">{c.challan_date}</td>
                          <td className="px-4 py-2 border">
                            {c.offense_details}
                          </td>
                          <td className="px-4 py-2 border">₹{c.amount}</td>
                          <td className="px-4 py-2 border">
                            <span
                              className={`px-2 py-1 rounded text-white text-xs ${
                                c.challan_status === "Pending"
                                  ? "bg-red-500"
                                  : "bg-green-500"
                              }`}
                            >
                              {c.challan_status}
                            </span>
                          </td>
                          <td className="px-4 py-2 border">{c.state}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => downloadPDF(apiResponseData.data)}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Download PDF
                </button>
                <button
                  onClick={() => setShowApiResponseModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </PortalModal>
      )}
    </div>
  );
}
